import React, { useEffect, useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import UserContext from "../../components/User_Context.js";
import * as menu from "../../assets/menus/menu.js";
import * as breadcrumb from "../../components/ui/breadcrumb.js";
import * as api from "../../api/apiConnector.js";
import { useTranslation } from "react-i18next";
import withReactContent from "sweetalert2-react-content";
import Swal from "sweetalert2";
import { LisLabels } from "../_label/lis_labels/lis_labels.js";

const SystemStatusMonitor = () => {
  const { currentUser } = useContext(UserContext);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const MySwal = withReactContent(Swal);

  // State to hold job status from backend
  const [jobStatus, setJobStatus] = useState("RUNNING");
  // Countdown timer for next refresh (in seconds)
  const [countdown, setCountdown] = useState(5);

  // Initialize i18n labels from centralized file
  const i18n = menu.getI18n();
  i18n.addResourceBundle("it", "lisLabels", LisLabels.it, true, true);
  i18n.addResourceBundle("en", "lisLabels", LisLabels.en, true, true);

  /**
   * Format a date for display (DD/MM/YYYY HH:mm)
   * @param {string|Date|null} date - Date to format
   * @returns {string} Formatted date string or 'N/A'
   */
  const formatDateTime = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleString(undefined, {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      });
    } catch {
      return "N/A";
    }
  };

  /**
   * Get status display properties based on current status
   * @param {string} status - 'IDLE' or 'RUNNING'
   * @param {string} lastStatus - 'OK' or 'ERROR'
   * @returns {object} Display properties for the status badge
   */
  const getStatusDisplay = (status, lastStatus) => {
    if (status === "RUNNING") {
      return {
        badge: t("statusRunningBadge", { ns: "lisLabels" }),
        icon: "fas fa-cog fa-spin",
        bg: "rgba(13, 110, 253, 0.15)",
        color: "#0d6efd",
      };
    }
    // IDLE status - color depends on last result
    if (lastStatus === "ERROR") {
      return {
        badge: t("statusIdleBadge", { ns: "lisLabels" }),
        icon: "fas fa-clock",
        bg: "rgba(220, 53, 69, 0.15)",
        color: "#dc3545",
      };
    }
    return {
      badge: t("statusIdleBadge", { ns: "lisLabels" }),
      icon: "fas fa-clock",
      bg: "rgba(25, 135, 84, 0.15)",
      color: "#198754",
    };
  };

  /**
   * Build status cards from backend data
   */
  const buildStatusCards = () => {
    if (!jobStatus) {
      // Return loading placeholders
      return [
        {
          id: "source-import",
          section: t("sourceDataImportSection", { ns: "lisLabels" }),
          title: t("lisToAiPlanLocalDbTitle", { ns: "lisLabels" }),
          statusBadge: t("loading", { ns: "lisLabels" }),
          statusIcon: "fas fa-spinner fa-spin",
          statusBg: "rgba(108, 117, 125, 0.15)",
          statusColor: "#6c757d",
          borderColor: "#0d6efd",
          lastStarted: "-",
          lastEnded: "-",
          lastStatus: "-",
          lastStatusColor: "#6c757d",
          details: t("loading", { ns: "lisLabels" }),
        },
        {
          id: "data-analysis",
          section: t("dataAnalysisVerificationSection", { ns: "lisLabels" }),
          title: t("postImportProcessingTitle", { ns: "lisLabels" }),
          statusBadge: t("loading", { ns: "lisLabels" }),
          statusIcon: "fas fa-spinner fa-spin",
          statusBg: "rgba(108, 117, 125, 0.15)",
          statusColor: "#6c757d",
          borderColor: "#198754",
          lastStarted: "-",
          lastEnded: "-",
          lastStatus: "-",
          lastStatusColor: "#6c757d",
          details: t("loading", { ns: "lisLabels" }),
        },
      ];
    }

    const sourceStatus = getStatusDisplay(
      jobStatus.SOURCE_STATUS,
      jobStatus.SOURCE_LAST_STATUS
    );
    const jobStatusDisplay = getStatusDisplay(
      jobStatus.JOB_STATUS,
      jobStatus.JOB_LAST_STATUS
    );

    return [
      {
        id: "source-import",
        section: t("sourceDataImportSection", { ns: "lisLabels" }),
        title: t("lisToAiPlanLocalDbTitle", { ns: "lisLabels" }),
        statusBadge: sourceStatus.badge,
        statusIcon: sourceStatus.icon,
        statusBg: sourceStatus.bg,
        statusColor: sourceStatus.color,
        borderColor: "#0d6efd",
        lastStarted: formatDateTime(jobStatus.SOURCE_LAST_RUN),
        lastEnded: formatDateTime(jobStatus.SOURCE_LAST_SUCCESS),
        lastStatus: jobStatus.SOURCE_LAST_STATUS || "N/A",
        lastStatusColor:
          jobStatus.SOURCE_LAST_STATUS === "OK"
            ? "#198754"
            : jobStatus.SOURCE_LAST_STATUS === "ERROR"
            ? "#dc3545"
            : "#6c757d",
        details:
          jobStatus.SOURCE_LAST_ERROR ||
          t("noErrorsReported", { ns: "lisLabels" }),
      },
      {
        id: "data-analysis",
        section: t("dataAnalysisVerificationSection", { ns: "lisLabels" }),
        title: t("postImportProcessingTitle", { ns: "lisLabels" }),
        statusBadge: jobStatusDisplay.badge,
        statusIcon: jobStatusDisplay.icon,
        statusBg: jobStatusDisplay.bg,
        statusColor: jobStatusDisplay.color,
        borderColor: "#198754",
        lastStarted: formatDateTime(jobStatus.JOB_LAST_RUN),
        lastEnded: formatDateTime(jobStatus.JOB_LAST_SUCCESS),
        lastStatus: jobStatus.JOB_LAST_STATUS || "N/A",
        lastStatusColor:
          jobStatus.JOB_LAST_STATUS === "OK"
            ? "#198754"
            : jobStatus.JOB_LAST_STATUS === "ERROR"
            ? "#dc3545"
            : "#6c757d",
        details:
          jobStatus.JOB_LAST_ERROR ||
          t("noErrorsReported", { ns: "lisLabels" }),
      },
    ];
  };

  // Fetch job status on mount and setup auto-refresh every 5 seconds
  useEffect(() => {
    // Set breadcrumb
    const path = [
      { link: "/dashboard", name: "Home" },
      { link: "/admin", name: "Admin" },
      { link: "/admin/system_status", name: "System Status Monitor" },
    ];
    breadcrumb.set(path);

    // Write menu to make it visible
    menu.write({ user: currentUser() });

    fetchJobStatus();

    // Countdown timer - decrements every second
    const countdownInterval = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          fetchJobStatus();
          return 5; // Reset countdown after fetch
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(countdownInterval);
  }, []);

  /**
   * Fetch job status from LIS_JOB_STATUS table
   */
  const fetchJobStatus = async () => {
    try {
      const response = await api.sendGetRequest(`admin/lis/status`, {
        user: currentUser(),
        silentMode: true,
      });
      if (response.success) {
        setJobStatus(response.data);
      }
    } catch (error) {
      console.error("Error fetching job status:", error);
    }
  };

  const statusCards = buildStatusCards();

  return (
    <>
      <div className="page-titles">
        <ol className="breadcrumb"></ol>
      </div>

      <div className="container-fluid ">
        <div className="card-body px-0 pb-5 pt-0 bg-white">
          <div className="table-responsive active-projects task-table">
            <div className="tbl-caption h-auto">
              <div className="row">
                <div className="col-12">
                  <div className="col-12">
                    <h3 className="mb-0">
                      <i className="fas fa-server me-2"></i>
                      {t("systemStatusMonitor", { ns: "lisLabels" })}
                    </h3>
                    <p className="text-muted">
                      {t("systemStatusMonitorDescription", { ns: "lisLabels" })}
                    </p>
                  </div>
                </div>
              </div>
              <div className=" ">
        <div className="row g-4">
          {statusCards.map((card) => (
            <div className="col-12 col-lg-6" key={card.id}>
              <div
                className="bg-white h-100 shadow rounded-3 p-4"
                style={{ borderLeft: `6px solid ${card.borderColor}` }}
              >
                <div className="d-flex justify-content-between align-items-start flex-wrap gap-3 mb-4">
                  <div>
                    <div className="text-uppercase text-muted small fw-semibold">
                      {card.section}
                    </div>
                    <h4 className="mb-1">{card.title}</h4>
                  </div>
                  <span
                    className="d-inline-flex align-items-center px-4 py-2 rounded-pill fw-semibold"
                    style={{
                      backgroundColor: card.statusBg,
                      color: card.statusColor,
                    }}
                  >
                    <i className={`${card.statusIcon} me-2`}></i>
                    {card.statusBadge}
                  </span>
                </div>
                <div className="row g-3">
                  <div className="col-12 col-md-6">
                    <div className="text-uppercase text-muted small fw-semibold">
                      {t("lastImportStarted", { ns: "lisLabels" })}
                    </div>
                    <div className="fw-semibold">{card.lastStarted}</div>
                  </div>
                  <div className="col-12 col-md-6">
                    <div className="text-uppercase text-muted small fw-semibold">
                      {t("lastImportEnded", { ns: "lisLabels" })}
                    </div>
                    <div className="fw-semibold">
                      {jobStatus === "RUNNING"
                        ? t("loading", { ns: "lisLabels" })
                        : card.lastEnded}
                    </div>
                  </div>
                  <div className="col-12">
                    <div className="text-uppercase text-muted small fw-semibold">
                      {t("lastStatus", { ns: "lisLabels" })}
                    </div>
                    <div
                      className="d-inline-flex align-items-center mt-1 px-3 py-1 rounded-pill bg-light fw-semibold"
                      style={{ color: card.lastStatusColor }}
                    >
                      <i
                        className={`fas ${
                          card.lastStatus === "OK"
                            ? "fa-check-circle"
                            : card.lastStatus === "ERROR"
                            ? "fa-times-circle"
                            : "fa-question-circle"
                        } me-2`}
                      ></i>
                      {card.lastStatus}
                    </div>
                  </div>
                </div>
                <div className="mt-4">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <div className="text-uppercase text-muted small fw-semibold">
                      {t("details", { ns: "lisLabels" })}
                    </div>
                  </div>
                  <div className="bg-light rounded-3 p-3 text-muted">
                    {card.details}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
            </div>
          </div>
        </div>
      </div>

      
    </>
  );
};

export default SystemStatusMonitor;
